/**
 * dt-card - Content container with header, body, footer slots
 * Variants: default, elevated, outlined
 *
 * Uses DOM node manipulation — not innerHTML serialization — so that child
 * custom elements (e.g. dt-button in the footer) are moved rather than
 * destroyed and re-created. This avoids the double-render bug where
 * re-parsed custom elements re-run connectedCallback on already-rendered
 * output.
 */
import { BaseElement } from '../base-element';

export class DtCard extends BaseElement {
  static observedAttributes = ['variant'];

  private _variant: 'default' | 'elevated' | 'outlined' = 'default';
  private _cardEl: HTMLElement | null = null;
  private _built = false;

  get variant(): string {
    return this._variant;
  }

  set variant(value: string) {
    if (['default', 'elevated', 'outlined'].includes(value)) {
      this._variant = value as 'default' | 'elevated' | 'outlined';
      if (this._cardEl) {
        this._cardEl.className = `card ${this.getVariantClasses()}`;
      }
    }
  }

  connectedCallback(): void {
    if (!this._built) {
      this.buildCard();
    }
  }

  attributeChangedCallback(name: string, _oldValue: string, newValue: string): void {
    if (name === 'variant') {
      this.variant = newValue || 'default';
    }
  }

  private getVariantClasses(): string {
    const base = 'rounded-xl bg-white overflow-hidden p-0';

    switch (this._variant) {
      case 'elevated':
        return `${base} shadow-lg`;
      case 'outlined':
        return `${base} border border-neutral-200`;
      default:
        return `${base} shadow-sm border border-neutral-100`;
    }
  }

  private buildCard(): void {
    const header = this.querySelector('[slot="header"]');
    const footer = this.querySelector('[slot="footer"]');

    // Collect body elements (direct children without a slot attribute)
    const bodyEls: Element[] = [];
    for (const child of Array.from(this.children)) {
      if (child === header || child === footer) continue;
      if (child.hasAttribute('slot')) continue;
      bodyEls.push(child);
    }

    // Build the card wrapper
    this._cardEl = document.createElement('div');
    this._cardEl.className = `card ${this.getVariantClasses()}`;

    if (header) {
      const headerDiv = document.createElement('div');
      headerDiv.className =
        'card-header border-b border-neutral-100 px-6 py-4 text-lg font-semibold text-neutral-900';
      headerDiv.appendChild(header); // moves the node
      this._cardEl.appendChild(headerDiv);
    }

    if (bodyEls.length > 0) {
      const bodyDiv = document.createElement('div');
      bodyDiv.className = 'card-body p-6';
      for (const el of bodyEls) {
        bodyDiv.appendChild(el); // moves each node
      }
      this._cardEl.appendChild(bodyDiv);
    }

    if (footer) {
      const footerDiv = document.createElement('div');
      footerDiv.className =
        'card-footer border-t border-neutral-100 bg-neutral-50 px-6 py-4 flex justify-end gap-3';
      // Move the footer wrapper's children (e.g. dt-button), not the wrapper itself
      while (footer.firstChild) {
        footerDiv.appendChild(footer.firstChild);
      }
      footer.remove();
      this._cardEl.appendChild(footerDiv);
    }

    // Clear any remaining whitespace text nodes and insert the card
    this.innerHTML = '';
    this.appendChild(this._cardEl);
    this._built = true;
  }
}

customElements.define('dt-card', DtCard);
